import React from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2, Trash2, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImageUrl } from "@/lib/api";
import { UploadControls, useImageUploads } from "@/components/UploadControls";
import { SampleBadge } from "@/components/ImageLibrary";

// Multi-image input (products): every add APPENDS; first image is the cover. Existing images are never dropped on add.
export default function MultiImageInput({ value = [], onChange: rawChange, label = "Images", testid = "multi-image", disabled = false, libraryCategory, librarySuggest, max = 10, captions = null, onCaptionsChange = null }) {
  const images = Array.isArray(value) ? value : [];
  // Never keep more than `max` photos; extra picks are dropped with a clear message.
  const onChange = (next) => {
    if (next.length > max) toast.error(`Maximum ${max} photos allowed — ${next.length - max} extra photo(s) skipped.`);
    rawChange(next.slice(0, max));
  };
  const full = images.length >= max;
  const latest = React.useRef(images);
  latest.current = images;
  const up = useImageUploads((urls) => onChange([...latest.current, ...urls.filter((u) => !latest.current.includes(u))]));
  const busy = up.busy;

  const append = (url) => { if (!images.includes(url)) onChange([...images, url]); };

  const removeAt = (idx) => {
    const url = images[idx];
    onChange(images.filter((_, i) => i !== idx));
    if (onCaptionsChange && captions && url in captions && !images.some((u, i) => i !== idx && u === url)) {
      const { [url]: _drop, ...rest } = captions;
      onCaptionsChange(rest);
    }
  };

  const move = (idx, dir) => {
    const next = [...images];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    onChange(next);
  };

  return (
    <div data-testid={`${testid}-wrap`}>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      <div className="mt-2 grid grid-cols-2 min-[360px]:grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`${testid}-grid`}>
        {images.map((img, i) => (
          <div key={i} className="min-w-0">
          <div className="relative group">
            <img
              src={resolveImageUrl(img)}
              alt=""
              className="w-full aspect-square object-cover rounded-xl border border-slate-200 bg-slate-100"
              onError={(e) => { e.currentTarget.src = resolveImageUrl(""); }}
              data-testid={`${testid}-item-${i}`}
            />
            <SampleBadge url={img} className="absolute bottom-9 sm:bottom-1 left-1" testid={`${testid}-sample-badge-${i}`} />
            {i === 0 && (
              <span className="absolute top-1 left-1 inline-flex items-center gap-0.5 rounded-full bg-amber-500 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5">
                <Star className="w-2.5 h-2.5" /> Cover
              </span>
            )}
            <button type="button" onClick={() => removeAt(i)} aria-label="Remove image" className="absolute -top-1 -right-1 w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 shadow flex items-center justify-center" data-testid={`${testid}-remove-${i}`}>
              <Trash2 className="w-3 h-3" />
            </button>
            <div className="absolute inset-x-0 bottom-0 flex justify-between sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="bg-black/60 text-white p-2.5 sm:p-1.5 rounded-tr-md disabled:opacity-30" aria-label="Move left" data-testid={`${testid}-left-${i}`}><ChevronLeft className="w-3 h-3" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === images.length - 1} className="bg-black/60 text-white p-2.5 sm:p-1.5 rounded-tl-md disabled:opacity-30" aria-label="Move right" data-testid={`${testid}-right-${i}`}><ChevronRight className="w-3 h-3" /></button>
            </div>
          </div>
          {onCaptionsChange && (
            <input value={(captions || {})[img] || ""} maxLength={60} placeholder="Caption (optional)"
              onChange={(e) => onCaptionsChange({ ...(captions || {}), [img]: e.target.value.slice(0, 60) })}
              aria-label={`Photo ${i + 1} caption`}
              className="mt-1 w-full min-w-0 border border-slate-200 rounded-lg px-2 py-1.5 sm:py-1 text-base sm:text-[11px] outline-none focus:border-emerald-500" data-testid={`${testid}-caption-${i}`} />
          )}
          </div>
        ))}
        {/* Add tile */}
        {!full && <div className="w-full aspect-square rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
        </div>}
      </div>

      <div className="mt-1 text-[11px] text-slate-500" data-testid={`${testid}-count`}>{images.length}/{max} photos · pehli photo cover hai{full ? " · limit poori" : ""}</div>
      {full ? null : <UploadControls multiple onFiles={(files) => { const room = max - images.length; if (files.length > room) toast.error(`Maximum ${max} photos allowed — sirf ${room} aur jod sakte hain.`); up.run(files.slice(0, room)); }} onUrl={append} libraryCategory={libraryCategory} librarySuggest={librarySuggest} busy={up.busy} progress={up.progress} failed={up.failed} onRetry={up.retry} disabled={disabled} testid={testid} />}
    </div>
  );
}
